import React, { memo, Fragment, useState, useEffect } from 'react';
import styled from 'styled-components';
import numeral from 'numeral';
import ImageFadeIn from 'react-image-fade-in';
import optionsForProductSummary from './utils/optionsForProductSummary';
import renderSelectedItemsString from '../../../utils/renderSelectedItemsString';
import ProductEditAndQuantityContainer from './ProductEditAndQuantity/ProductEditAndQuantityContainer';
import {
  Subhead,
  Caption1,
  Caption2,
  Body,
} from '../../../components/Typography';
import CalorieString from '../../../components/CalorieString';
import appConfig from '../../../../app-config';

const Wrapper = styled.div`
  padding: ${(props) => (props.isForPreview ? '24px 0' : '24px 20px 24px 0')};
  grid-gap: 14px 24px;
  border-bottom: ${(props) =>
    props.isLastProductInCart
      ? 'none'
      : `1px solid ${props.theme.productSummaryBorderColor}`};
  @media (max-width: 786px) {
    grid-template-columns: 80px 1fr;
  }
`;

const TitleAndPriceWrapper = styled.div`
  display: grid;
  align-content: stretch;
  align-self: stretch;
`;

const ProductTitle = styled(Subhead)`
  display: grid;
  grid-template-rows: auto auto;
  //align-items: center;
  align-content: center;
  text-transform: capitalize;
`;

const ProductImage = styled(ImageFadeIn)`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 50%;
`;

const OptionWrapper = styled.span`
  padding: 5px 2px;
  @media (max-width: 786px) {
    grid-template-columns: 80px 1fr;
  }
`;
const InfoMessageWrapper = styled.div`
  display: grid;
  grid-template-columns: 10px 1fr;
  grid-gap: 10px;
  padding: 5px 10px;
  background-color: #fee7cd;
  border-radius: 7px;
  white-space: pre-wrap;
  margin-top: 10px;
`;
const InfoIcon = styled.i`
  margin-top: 4px;
  color: #fe8e0f;
`;
const Label = styled.span`
  //color: ${(props) => props.theme.productLabelColor};
  font-family: ${(props) => props.theme.primaryFontFontFamily};
  color: black;
  font-weight: 600;
`;

const Text = styled.span`
  // text-transform: capitalize;
  font-family: ${(props) => props.theme.primaryFontFontFamily};
  span {
    font-size: 13px;
    font-weight: 500;
  }
`;

const EditQuantityRow = styled.div`
  grid-column: span 2;
  margin-top: 6px;
`;

const Price = styled(Caption2)`
  color: #000;
  font-weight: 300;
`;

const CalPriceRow = styled.div``;

const Dot = () => (
  <i
    style={{
      fontSize: '3px',
      textAlign: 'center',
      margin: '0 5px',
      verticalAlign: 'middle',
    }}
    className='fas fa-circle'
  />
);

const ImageTitleDiv = styled.div`
  display: grid;
  margin-bottom: 12px;
  grid-template-columns: ${(props) => (props.isNoImage ? null : '80px 1fr')};
`;

const ProductSummary = ({
  productInCart,
  isLastProductInCart,
  isForPreview,
  validatedCart,
  isForCombo,
}) => {
  const {
    flatProduct,
    totalPrice,
    singlePrice,
    unavailableItems,
  } = productInCart;
  const [unavailableItemsMessage, setUnavailableItemsMessage] = useState('');
  useEffect(() => {
    if (unavailableItems?.length > 0) {
      const message = `Your previous ${
        unavailableItems?.length > 1 ? 'items have' : 'item has'
      } been removed as ${
        unavailableItems?.length > 1 ? 'they are' : 'it is'
      } not available:\n- ${unavailableItems.join('\n- ')} `;
      setUnavailableItemsMessage(message);
    }
  }, [unavailableItems]);
  return (
    <Wrapper
      key={flatProduct.product_id}
      isLastProductInCart={isLastProductInCart || isForCombo}
      isForPreview={isForPreview}
    >
      <ImageTitleDiv isNoImage={appConfig.isNoImage}>
        {!appConfig.isNoImage && <ProductImage src={flatProduct.image_url} />}
        <ProductTitle id='ProductSummary-Title'>
          {flatProduct.name}
          {validatedCart && flatProduct.quantity > 1 && (
            <span style={{ textTransform: 'lowercase' }}>
              {' '}
              x{flatProduct.quantity}
            </span>
          )}
          {!isForCombo && (
            <CalPriceRow cals isForPreview={isForPreview}>
              <Price id='ProductSummary-Price'>
                {numeral(singlePrice).format('$0.00')}
              </Price>
              {flatProduct.calories ? (
                <Fragment>
                  <CalorieString
                    id='ProductSummary-Calories'
                    calories={flatProduct.calories.find(
                      (calorie) => calorie.variant_id === flatProduct.variant_id
                    )}
                    caloriesFontSize={'13px'}
                  />
                </Fragment>
              ) : (
                <div />
              )}
            </CalPriceRow>
          )}
        </ProductTitle>
      </ImageTitleDiv>

      <div>
        {flatProduct.prices.length > 1 && (
          <OptionWrapper isForPreview={isForPreview}>
            <Label id='ProductSummary-Size'>Size</Label> -
            <Text>{flatProduct.variant_name}</Text>
          </OptionWrapper>
        )}
        {optionsForProductSummary(flatProduct) &&
          optionsForProductSummary(flatProduct).map((option, index) => {
            const isLastOption =
              optionsForProductSummary(flatProduct).length === index + 1;
            if (option.sub_options.length) {
              return option.sub_options.map((subOption) => {
                return (
                  <OptionWrapper
                    isLastOption={isLastOption}
                    key={subOption.option_id}
                    isForPreview={isForPreview}
                  >
                    <Label id='ProductSummary-SubOptionName'>
                      {subOption.name}
                    </Label>
                    <span style={{ margin: '0 2px' }}>-</span>
                    <Text>
                      {renderSelectedItemsString(flatProduct, subOption)}
                    </Text>
                  </OptionWrapper>
                );
              });
            } else {
              return (
                <OptionWrapper
                  isLastOption={isLastOption}
                  key={option.option_id}
                  isForPreview={isForPreview}
                >
                  <Label id='ProductSummary-OptionName'>{option.name}</Label>
                  <span style={{ margin: '0 2px' }}>-</span>
                  <Text>{renderSelectedItemsString(flatProduct, option)}</Text>
                </OptionWrapper>
              );
            }
          })}
      </div>
      <div>
        {unavailableItemsMessage && (
          <InfoMessageWrapper>
            <InfoIcon className='fa fa-info-circle'></InfoIcon>
            {unavailableItemsMessage}
          </InfoMessageWrapper>
        )}
      </div>
      {!isForCombo && (
        <EditQuantityRow>
          <ProductEditAndQuantityContainer productInCart={productInCart} />
        </EditQuantityRow>
      )}
    </Wrapper>
  );
};

export default ProductSummary;
